import { useCallback, useContext, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { CaretDown, CaretUp } from "@magnetic/icons";
import { Popover } from "@magnetic/popover";

import { Button } from "src/components/buttons";
import { ApiError } from "src/core";
import {
  CandidateContext,
  DataProviderContext
} from "src/core/provider/dataProvider";
import { CANDIDATE_NAME, CandidateMode, ConfigD } from "src/utils/api/configD";

import { CommitCandidateForm } from "./CommitCandidateForm";

interface CommitCandidateButtonProps {
  readonly onSuccess: () => void;
}

export const CommitCandidateButton = ({
  onSuccess
}: CommitCandidateButtonProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | undefined>();
  const ref = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => {
    setOpen((last) => {
      return !last;
    });
  }, []);

  const { fabricId = "", dciId = "" } = useParams();
  const { fabric, setMode } = useContext(DataProviderContext);

  // Determine context based on which ID is present
  const context: CandidateContext = fabricId ? "fabric" : "multisite";
  const contextId = fabricId || dciId;
  const entityName =
    context === "fabric" ? fabric?.name || "Fabric" : "Multi-Site";

  // Commit handler
  const onCommit = useCallback(
    (comment: string): Promise<void> => {
      const handleSuccess = () => {
        setMode(CandidateMode.Running);
        onSuccess();
      };
      const handleFailure = (err: ApiError) => {
        setError(err);
        return Promise.reject(err);
      };
      if (context === "fabric") {
        return ConfigD.commitFabricCandidate(
          contextId,
          CANDIDATE_NAME,
          comment
        ).then(handleSuccess, handleFailure);
      } else {
        return ConfigD.commitDciCandidate(
          contextId,
          CANDIDATE_NAME,
          comment
        ).then(handleSuccess, handleFailure);
      }
    },
    [context, contextId, onSuccess, setMode]
  );

  return (
    <Button name="commit-button" kind="secondary" onClick={toggle} ref={ref}>
      Comment and push
      <Popover
        className="comment-push-popover"
        root={ref.current}
        placement="top"
        kind="map"
        size="lg"
        open={open}
        title={
          <CommitCandidateForm
            error={error}
            onClose={() => setOpen(false)}
            onCommit={onCommit}
            entityName={entityName}
          />
        }
      >
        {open ? <CaretDown /> : <CaretUp />}
      </Popover>
    </Button>
  );
};
